import apiService from './apiService';

/**
 * „Übergabe an Mitarbeiter“ (contract livecenter §5, Einstellungen): the
 * WhatsApp agent can hand a chat to the salon team.
 */
export interface HandoffSettings {
  enabled: boolean;
  /** The agent offers the handoff itself when it gets stuck (e.g. a complaint). */
  offer_on_problems: boolean;
  /** Nobody answers a requested handoff within this → the agent steps back in. */
  wait_minutes: number;
  /** Quiet chat after a takeover → silently back to the agent. */
  idle_minutes: number;
  /** null = the standard text (Du/Sie from the salon's setting). */
  busy_message: string | null;
}

export type HandoffField = keyof HandoffSettings;

/** Order of the fields in the card (and in the "Eigene Werte" hint). */
export const HANDOFF_FIELDS: readonly HandoffField[] = [
  'enabled',
  'offer_on_problems',
  'wait_minutes',
  'idle_minutes',
  'busy_message',
];

export const HANDOFF_LIMITS = {
  waitMinutes: { min: 1, max: 30 },
  idleMinutes: { min: 2, max: 60 },
  busyMessageMax: 700,
} as const;

export const DEFAULT_HANDOFF_SETTINGS: HandoffSettings = {
  enabled: false,
  offer_on_problems: true,
  wait_minutes: 5,
  idle_minutes: 5,
  busy_message: null,
};

export interface WhatsappSettings {
  success: boolean;
  greeting_text: string | null;
  /** Missing on a backend without the livecenter. */
  handoff?: HandoffSettings | null;
  /**
   * With `phone_number`: per FIELD NAME whether the value is the company's
   * (true) or set for this agent number — flat, handoff fields included
   * (`wait_minutes`, …; contract livecenter §5).
   */
  inherited?: Record<string, boolean>;
  error?: string;
}

export interface WhatsappSettingsUpdate {
  greeting_text?: string;
  /** Only the fields sent change; for an agent number they become its own values. */
  handoff?: Partial<HandoffSettings>;
  /** With an agent number: inherit again — a field name, or `handoff` for the whole block. */
  reset_fields?: string[];
}

class WhatsappSettingsService {
  /** Without `phoneNumber` the company defaults, with one what that agent actually uses. */
  async getSettings(phoneNumber?: string | null): Promise<WhatsappSettings> {
    const q = phoneNumber ? `?phone_number=${encodeURIComponent(phoneNumber)}` : '';
    return apiService.get<WhatsappSettings>(`/whatsapp/settings${q}`);
  }

  /** With `phoneNumber` this stores an override for that agent only (like /voice/settings). */
  async updateSettings(settings: WhatsappSettingsUpdate, phoneNumber?: string | null): Promise<WhatsappSettings> {
    const body = phoneNumber ? { ...settings, phone_number: phoneNumber } : settings;
    return apiService.put<WhatsappSettings>('/whatsapp/settings', body);
  }
}

export const whatsappSettingsService = new WhatsappSettingsService();
export default whatsappSettingsService;
